import {
    Box, CardHeader, CardContent, Grid, FormHelperText, Stack, TextField,
    Tabs, Tab, InputLabel, Button,
    Paper
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ADMIN_ROLES } from "../../../utils/constants";
import { Form, FormikProvider, useFormik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import errorHandler from "../../../utils/errorHandler";
import CardLayout from "../../../layouts/CardLayout";
import { toast } from "react-toastify";
import LoadingButton from "../../../components/Buttons/LoadingButton";
import { AboutUsApi } from "../../../services/AllServices";
import EdiorJodit from '../../../components/editor/editorJodit';
import { IMAGE_BASE } from "../../../utils/constants";

const AboutUs = () => {
    const user = useSelector((state) => state.auth.user);
    const isReadOnly = user?.role === ADMIN_ROLES.MGT || user?.role === ADMIN_ROLES.JUNIOR;
    const { id } = useParams();
    const pageType = id ? "Update" : "Update";
    const [cmsData, setCmsData] = useState({});
    const [tab, setTab] = useState(0);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [missionPreview, setMissionPreview] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [services, setServices] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await AboutUsApi.getById();
                
                setCmsData(data.data);
                setMissionPreview(IMAGE_BASE + data?.data?.missionImage);
                setBannerPreview(IMAGE_BASE + data?.data?.bannerImage);

                if (data.data.sectionItems && Array.isArray(data.data.sectionItems)) {
                    const formattedTeam = data.data.sectionItems.map((member) => ({
                        ...member,
                        post: member.post,
                        preview: IMAGE_BASE + member.image
                    }));
                    setTeamMembers(formattedTeam);
                }

                if (data.data.serviceItems && Array.isArray(data.data.serviceItems)) {
                    const formattedServices = data.data.serviceItems.map((service) => ({
                        ...service,
                        post: service.post,
                        preview: IMAGE_BASE + service.image
                    }));
                    setServices(formattedServices);
                }
            } catch (error) {
                errorHandler(error);
            }
        };
        fetchData();
    }, []);

    // ✅ Validation Schema
    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        ourTeamTitle: Yup.string().required("Team title is required"),
        outTeamSubTitle: Yup.string().required("Team subtitle is required"),
        teamMembers: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required("Name is required"),
                post: Yup.string().required("Post is required"),
            })
        ),
        ourServiceTitle: Yup.string().required("Service title is required"),
        services: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required("Name is required"),
                post: Yup.string().required("Post is required"),
            })
        )
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: cmsData?.title || "",
            bannerDescription: cmsData?.bannerDescription || "",
            bannerImage: cmsData?.bannerImage || null,
            missionDescription: cmsData?.missionDescription || "",
            missionImage: cmsData?.missionImage || null,
            teamMembers: cmsData?.sectionItems || [],
            ourTeamTitle: cmsData?.ourTeamTitle || "",
            outTeamSubTitle: cmsData?.outTeamSubTitle || "",
            services: cmsData?.services || [],
            ourServiceTitle: cmsData?.ourServiceTitle || "",
        },
        validationSchema,
        onSubmit: async (values, helpers) => {
            const touched = {};
            Object.keys(values).forEach(key => touched[key] = true);
            formik.setTouched(touched);

            try {
                await validationSchema.validate(values, { abortEarly: false });
                await handleSubmit(values, helpers);
            } catch (validationErrors) {
                const firstErrorKey = Object.keys(formik.errors)[0];

                if (["title", "bannerDescription"].includes(firstErrorKey)) setTab(0);
                else if (["missionDescription"].includes(firstErrorKey)) setTab(1);
                else if (["ourTeamTitle", "outTeamSubTitle", "teamMembers"].includes(firstErrorKey)) setTab(2);
                else if (["ourServiceTitle", "services"].includes(firstErrorKey)) setTab(3);
            }
        },
    });

    const handleTabChange = (event, newValue) => setTab(newValue);

    const handleImageUpload = (event, field) => {
        const file = event.currentTarget.files[0];
        if (file) {
            formik.setFieldValue(field, file);
            const previewURL = URL.createObjectURL(file);
            if (field === "bannerImage") setBannerPreview(previewURL);
            if (field === "missionImage") setMissionPreview(previewURL);
        }
    };

    const handleAddTeamMember = () => {
        const updated = [...teamMembers, { image: null, name: "", post: "", preview: null }];
        setTeamMembers(updated);
        formik.setFieldValue("teamMembers", updated);
    };

    const handleRemoveTeamMember = (index) => {
        const updated = [...teamMembers];
        updated.splice(index, 1);
        setTeamMembers(updated);
        formik.setFieldValue("teamMembers", updated);
    };

    const handleTeamMemberChange = (index, field, value) => {
        const updated = [...teamMembers];
        updated[index][field] = value;
        setTeamMembers(updated);
        formik.setFieldValue("teamMembers", updated);
    };

    const handleTeamImageUpload = (index, file) => {
        const updated = [...teamMembers];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setTeamMembers(updated);
        formik.setFieldValue("teamMembers", updated);
    };

    const handleAddService = () => {
        const updated = [...services, { image: null, name: "", post: "", preview: null }];
        setServices(updated);
        formik.setFieldValue("services", updated);
    };

    const handleRemoveService = (index) => {
        const updated = [...services];
        updated.splice(index, 1);
        setServices(updated);
        formik.setFieldValue("services", updated);
    };

    const handleServiceChange = (index, field, value) => {
        const updated = [...services];
        updated[index][field] = value;
        setServices(updated);
        formik.setFieldValue("services", updated);
    };

    const handleServiceImageUpload = (index, file) => {

        const updated = [...services];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setServices(updated);
        formik.setFieldValue("services", updated);
    };

    const hasTabErrors = (fields) =>
        fields.some((field) => {
            const error = formik.errors[field];
            if (error) return true;

            if (field === "teamMembers" && Array.isArray(formik.errors.teamMembers)) {
                return formik.errors.teamMembers.some((e) => e?.name || e?.post);
            }

            return false;
        });

    async function handleSubmit(values, { setSubmitting }) {
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("bannerDescription", values.bannerDescription);
            formData.append("missionDescription", values.missionDescription);
            formData.append("ourTeamTitle", values.ourTeamTitle);
            formData.append("outTeamSubTitle", values.outTeamSubTitle);
            formData.append("ourServiceTitle", values.ourServiceTitle);
            formData.append("imagePath", "cms");

            if (values.bannerImage instanceof File) {
                formData.append("bannerImage", values.bannerImage);
            }

            if (values.missionImage instanceof File) {
                formData.append("missionImage", values.missionImage);
            }

            if (id) formData.append("id", id);

            // const teamItems = teamMembers.map(member => ({
            //     name: member.name,
            //     post: member.post,
            // }));

            // formData.append("sectionItems", JSON.stringify(teamItems));

            // teamMembers.forEach((member) => {
            //     if (member.image instanceof File) {
            //         formData.append("sectionImages", member.image);
            //     }
            // });

            teamMembers.forEach((member, index) => {
                formData.append(`sectionItems[${index}][name]`, member.name);
                formData.append(`sectionItems[${index}][post]`, member.post);

                if (member.image instanceof File) {
                    formData.append(`sectionItems[${index}][image]`, member.image);
                }
            });

            services.forEach((member, index) => {
                formData.append(`serviceItems[${index}][name]`, member.name);
                formData.append(`serviceItems[${index}][post]`, member.post);

                if (member.image instanceof File) {
                    formData.append(`serviceItems[${index}][image]`, member.image);
                }
            });


            const resp = await AboutUsApi.update(formData);
            if (resp.data.status) {
                toast.success(resp.data.message);
                // navigate(-1);
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            errorHandler(error);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Box>
            <CardLayout>
                <CardHeader title={`Update About Us`} />
                <CardContent>
                    <Tabs value={tab} onChange={handleTabChange}>
                        <Tab
                            // label={"About Us Banner"}
                            label={hasTabErrors(["title", "bannerDescription"]) ? "About Us Banner*" : "About Us Banner"}
                            sx={{ color: hasTabErrors(["title", "bannerDescription"]) ? "error.main" : "inherit" }}
                        />
                        <Tab
                            // label="Who We Are"
                            label={hasTabErrors(["missionDescription"]) ? "Who We Are*" : "Who We Are"}
                            sx={{ color: hasTabErrors(["missionDescription"]) ? "error.main" : "inherit" }}
                        />
                        <Tab
                            // label="Our Team"
                            label={hasTabErrors(["ourTeamTitle", "outTeamSubTitle", "teamMembers"]) ? "Our Team*" : "Our Team"}
                            sx={{ color: hasTabErrors(["ourTeamTitle", "outTeamSubTitle", "teamMembers"]) ? "error.main" : "inherit" }}
                        />
                        <Tab
                            // label="Our Team"
                            label={hasTabErrors(["ourServiceTitle", "services"]) ? "Our Services*" : "Our Services"}
                            sx={{ color: hasTabErrors(["ourServiceTitle", "services"]) ? "error.main" : "inherit" }}
                        />
                    </Tabs>

                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                            {tab === 0 && (
                                <Grid container spacing={3} direction="column">
                                    <br />
                                    <FormField name="title" label="Title" formik={formik} disabled={isReadOnly} />
                                    {/* Banner Image */}
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={2}>
                                            <InputLabel>Banner Image</InputLabel>
                                            {bannerPreview && (
                                                <Box>
                                                    <img
                                                        src={bannerPreview}
                                                        alt="Banner Preview"
                                                        style={{
                                                            width: "100%",
                                                            maxHeight: 250,
                                                            objectFit: "cover",
                                                            borderRadius: 8,
                                                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            {!isReadOnly && (
                                                <Button variant="contained" component="label">
                                                    Upload
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={(e) => handleImageUpload(e, "bannerImage")}
                                                    />
                                                </Button>
                                            )}
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <label>Description</label><br /><br />
                                        <EdiorJodit
                                            formik={formik}
                                            fieldName="bannerDescription"
                                            onChange={(val) => !isReadOnly && formik.setFieldValue("bannerDescription", val)}
                                            data={formik?.values?.bannerDescription}
                                            readOnly={isReadOnly}
                                        />
                                        {formik.touched.bannerDescription && formik.errors.bannerDescription && (
                                            <FormHelperText error>{formik.errors.bannerDescription}</FormHelperText>
                                        )}
                                    </Grid>
                                </Grid>
                            )}

                            {tab === 1 && (
                                <Grid container spacing={3}>
                                    {/* Mission Section */}
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={2}>
                                            <InputLabel>Mission Image</InputLabel>

                                            {missionPreview && (
                                                <Box>
                                                    <img
                                                        src={missionPreview}
                                                        alt="Mission Preview"
                                                        style={{
                                                            width: "100%",
                                                            maxHeight: 250,
                                                            objectFit: "cover",
                                                            borderRadius: 8,
                                                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            {!isReadOnly && (
                                                <Button variant="contained" component="label">
                                                    Upload
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={(e) => handleImageUpload(e, "missionImage")}
                                                    />
                                                </Button>
                                            )}
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={2}>
                                            <InputLabel>Mission Description</InputLabel>
                                            <EdiorJodit
                                                formik={formik}
                                                fieldName="missionDescription"
                                                onChange={(val) => !isReadOnly && formik.setFieldValue("missionDescription", val)}
                                                data={formik?.values?.missionDescription}
                                                readOnly={isReadOnly}
                                            />
                                            {formik.touched.missionDescription &&
                                                formik.errors.missionDescription && (
                                                    <FormHelperText error>
                                                        {formik.errors.missionDescription}
                                                    </FormHelperText>
                                                )}
                                        </Stack>
                                    </Grid>
                                </Grid>

                            )}

                            {tab === 2 && (
                                <Grid container spacing={3} direction="column">
                                    <br />
                                    <FormField name="ourTeamTitle" label="Title" formik={formik} disabled={isReadOnly} />
                                    <FormField name="outTeamSubTitle" label="Sub Title" formik={formik} disabled={isReadOnly} />
                                    <br />
                                    <Grid container spacing={3}>
                                        {teamMembers.map((member, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                                                    {/* Upload image */}
                                                    <Stack spacing={1} alignItems="center">

                                                        {member.preview && (
                                                            <img
                                                                src={member.preview}
                                                                alt="Preview"
                                                                style={{
                                                                    width: 80,
                                                                    height: 80,
                                                                    objectFit: "cover",
                                                                    borderRadius: "50%",
                                                                }}
                                                            />
                                                        )}
                                                        {!isReadOnly && (
                                                            <Button variant="contained" component="label">
                                                                Upload Image
                                                                <input
                                                                    type="file"
                                                                    hidden
                                                                    accept="image/*"
                                                                    onChange={(e) => handleTeamImageUpload(index, e.target.files[0])}
                                                                />
                                                            </Button>
                                                        )}
                                                    </Stack>

                                                    {/* Inputs */}
                                                    <TextField
                                                        fullWidth
                                                        label="Name"
                                                        margin="normal"
                                                        disabled={isReadOnly}
                                                        value={member.name}
                                                        onChange={(e) =>
                                                            handleTeamMemberChange(index, "name", e.target.value)
                                                        }
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="Post"
                                                        disabled={isReadOnly}
                                                        value={member.post}
                                                        onChange={(e) =>
                                                            handleTeamMemberChange(index, "post", e.target.value)
                                                        }
                                                    />

                                                    {!isReadOnly && (
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            color="error"
                                                            sx={{ mt: 1 }}
                                                            onClick={() => handleRemoveTeamMember(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <br />
                                    {!isReadOnly && <Button variant="outlined" onClick={handleAddTeamMember}>+ Add Team Member</Button>}
                                </Grid>
                            )}

                            {tab === 3 && (
                                <Grid container spacing={3} direction="column">
                                    <br />
                                    <FormField name="ourServiceTitle" label="Main Title" formik={formik} disabled={isReadOnly} />
                                    <br />
                                    <Grid container spacing={3}>
                                        {services.map((member, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                                                    {/* Upload icon */}
                                                    <Stack spacing={1} alignItems="center">
                                                        {member.preview && (
                                                            <img
                                                                src={member.preview}
                                                                alt="Icon Preview"
                                                                style={{
                                                                    width: 50,
                                                                    height: 50,
                                                                    objectFit: "contain",
                                                                }}
                                                            />
                                                        )}

                                                        {!isReadOnly && (
                                                            <Button variant="contained" component="label">
                                                                Upload Icon
                                                                <input
                                                                    type="file"
                                                                    hidden
                                                                    accept="image/*"
                                                                    onChange={(e) => handleServiceImageUpload(index, e.target.files[0], "icon")}
                                                                />
                                                            </Button>
                                                        )}
                                                    </Stack>

                                                    {/* Inputs */}
                                                    <TextField
                                                        fullWidth
                                                        label="Title"
                                                        margin="normal"
                                                        disabled={isReadOnly}
                                                        value={member.name}
                                                        onChange={(e) => handleServiceChange(index, "name", e.target.value)}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="Description"
                                                        multiline
                                                        minRows={3}
                                                        disabled={isReadOnly}
                                                        value={member.post}
                                                        onChange={(e) => handleServiceChange(index, "post", e.target.value)}
                                                    />

                                                    {!isReadOnly && (
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            color="error"
                                                            sx={{ mt: 1 }}
                                                            onClick={() => handleRemoveService(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <br />
                                    {!isReadOnly && <Button variant="outlined" onClick={handleAddService}>+ Add Section</Button>}
                                </Grid>
                            )}


                            {!isReadOnly && (
                                <Stack spacing={2} mt={4} direction="row" justifyContent="flex-end">
                                    <LoadingButton text={pageType} loading={formik.isSubmitting} />
                                </Stack>
                            )}
                        </Form>
                    </FormikProvider>
                </CardContent>
            </CardLayout>
        </Box>
    );
};

const FormField = ({ name, label, formik, disabled }) => (
    <Grid item xs={12}>
        <TextField
            fullWidth
            name={name}
            label={label}
            value={formik.values[name]}
            onChange={formik.handleChange}
            required
            disabled={disabled}
            error={Boolean(formik.touched[name] && formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
        />
    </Grid>
);

export default AboutUs;
